"use client"

import { useState, useMemo } from "react"
import { generateAvatarDataUri } from "@/lib/avatar"

interface CommunityAvatarProps {
  /** Remote avatar URL (may be empty/falsy) */
  src: string | undefined
  /** Display name — used for fallback initials + alt text */
  name: string
  /** Tailwind size class, e.g. "w-8 h-8". Defaults to "w-8 h-8" */
  sizeClass?: string
  /** Extra classes on the outer wrapper */
  className?: string
}

/**
 * Unified avatar for all community components.
 *
 * - If `src` is a valid URL and loads successfully, renders the remote image.
 * - Otherwise renders a deterministic colored-initial SVG via data URI (instant, no network).
 * - `onError` auto-falls back so broken remote URLs degrade gracefully.
 */
export function CommunityAvatar({
  src,
  name,
  sizeClass = "w-8 h-8",
  className,
}: CommunityAvatarProps) {
  const [imgError, setImgError] = useState(false)

  const fallback = useMemo(
    () => generateAvatarDataUri(name || "?"),
    [name],
  )

  const showFallback = !src || imgError
  const resolvedSrc = showFallback ? fallback : src

  return (
    <img
      src={resolvedSrc}
      alt={name}
      className={`${sizeClass} rounded-full object-cover bg-muted shrink-0 ${className ?? ""}`}
      onError={showFallback ? undefined : () => setImgError(true)}
    />
  )
}
